import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

const DEFAULT_PAGE = 1;

function parsePositiveInt(raw: string | null, fallback: number): number {
  if (raw === null || raw === '') {
    return fallback;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed >= 1 ? Math.floor(parsed) : fallback;
}

/**
 * Persists pagination in the URL so back/forward and remount restore the same page.
 */
export function usePagedListUrlState(defaultPageSize = 10): {
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  resetPage: () => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();

  // React Router v7 recreates setSearchParams whenever the URL changes and builds
  // its "previous params" from the value captured at that render. Keep the latest
  // params and setter in refs so the memoized patch callbacks below always merge
  // against the current query string instead of the one from the first render
  // (otherwise paginating drops filters/markers added after the page mounted).
  const searchParamsRef = useRef(searchParams);
  const setSearchParamsRef = useRef(setSearchParams);
  useLayoutEffect(() => {
    searchParamsRef.current = searchParams;
    setSearchParamsRef.current = setSearchParams;
  }, [searchParams, setSearchParams]);

  const page = useMemo(
    () => parsePositiveInt(searchParams.get('page'), DEFAULT_PAGE),
    [searchParams],
  );

  const pageSize = useMemo(
    () => parsePositiveInt(searchParams.get('pageSize'), defaultPageSize),
    [searchParams, defaultPageSize],
  );

  const patchParams = useCallback(
    (updates: { page?: number; pageSize?: number }) => {
      const next = new URLSearchParams(searchParamsRef.current);
      if (updates.page !== undefined) {
        if (updates.page <= DEFAULT_PAGE) {
          next.delete('page');
        } else {
          next.set('page', String(updates.page));
        }
      }
      if (updates.pageSize !== undefined) {
        if (updates.pageSize === defaultPageSize) {
          next.delete('pageSize');
        } else {
          next.set('pageSize', String(updates.pageSize));
        }
      }
      setSearchParamsRef.current(next, { replace: true });
    },
    [defaultPageSize],
  );

  const setPage = useCallback((nextPage: number) => {
    patchParams({ page: nextPage });
  }, [patchParams]);

  const setPageSize = useCallback((nextPageSize: number) => {
    patchParams({ pageSize: nextPageSize, page: DEFAULT_PAGE });
  }, [patchParams]);

  const resetPage = useCallback(() => {
    patchParams({ page: DEFAULT_PAGE });
  }, [patchParams]);

  return { page, pageSize, setPage, setPageSize, resetPage };
}
